/** @jsxImportSource @emotion/react */
import { css, Global } from '@emotion/react';
import { ModalContext, ModalOptions } from '@/hooks/modal.hook';
import { ReactElement, ReactNode, useCallback, useState } from 'react';
import { Modal } from 'antd';
import { isDesktop } from 'react-device-detect';

interface ModalProviderProps {
  children: ReactNode;
}

export default function ModalProvider({ children }: ModalProviderProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [option, setOption] = useState({
    closable: true,
    centered: true,
    width: 611
  });
  const [modalContent, setModalContent] = useState<ReactElement | null>(null);
  const [onCloseCallback, setOnCloseCallback] = useState<(() => void) | null>(
    null
  );

  const openModal = useCallback(
    (
      content: ReactElement,
      options: ModalOptions = {
        showCloseIcon: true,
        autoClose: false,
        centered: true,
        onClose: () => {},
        width: 611
      }
    ) => {
      const {
        showCloseIcon = true,
        autoClose = false,
        centered = true,
        onClose = () => {},
        width
      } = options;

      setOption({
        closable: showCloseIcon,
        centered,
        width: width ?? 611
      });
      setModalContent(content);
      setIsOpen(true);
      setOnCloseCallback(() => onClose);

      if (autoClose) {
        setTimeout(() => {
          if (onCloseCallback) {
            onCloseCallback();
          }
          setIsOpen(false);
        }, 2000);
      }
    },
    [onCloseCallback]
  );

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setModalContent(null);
    if (onCloseCallback) {
      onCloseCallback();
    }
  }, [onCloseCallback]);

  const changeModalContent = useCallback(
    (
      content: ReactElement,
      options: ModalOptions = {
        showCloseIcon: true,
        autoClose: false,
        centered: true,
        onClose: () => {}
      }
    ) => {
      const {
        showCloseIcon = true,
        autoClose = false,
        centered = true,
        width
      } = options;

      setModalContent(content);

      setOption({
        closable: showCloseIcon,
        centered,
        width: width ?? 611
      });

      if (autoClose) {
        setTimeout(() => {
          if (onCloseCallback) {
            onCloseCallback();
          }
          setIsOpen(false);
        }, 2000);
      }
    },
    [onCloseCallback]
  );

  return (
    <ModalContext.Provider
      value={{
        openModal,
        closeModal,
        changeModalContent,
        isOpen,
        onClose: closeModal
      }}
    >
      {children}
      <Global styles={modalGlobalStyles(option.centered)} />
      <Modal
        css={isDesktop ? rootDesktopStyles : rootMobileStyles(option.centered)}
        destroyOnClose
        open={isOpen}
        onCancel={closeModal}
        closable={option.closable}
        aria-labelledby='modal-title'
        aria-describedby='modal-description'
        centered={option.centered}
        width={option.width}
        footer={null}
      >
        {modalContent}
      </Modal>
    </ModalContext.Provider>
  );
}

const modalGlobalStyles = (centered: boolean) => css`
  .ant-modal-wrap {
    display: ${centered ? 'block' : 'flex'};
    align-items: flex-end;
  }

  .ant-modal-content {
    border-radius: 1.6rem !important;
  }
`;

const rootMobileStyles = (centered: boolean) => css`
  max-width: ${centered ? 'unset' : '100% !important'};
  margin: ${centered ? '8px auto' : '0 !important'};
  top: ${centered ? '100px' : 'unset !important'};
  padding-bottom: ${centered ? '2.4rem' : '0 !important'};

  .ant-modal-content {
    margin: ${centered ? '0 1rem' : '0 !important'};
    border-bottom-left-radius: ${centered ? '1.6rem' : 0};
    border-bottom-right-radius: ${centered ? '1.6rem' : 0};
    padding: ${centered ? '2.4rem' : '1.6rem !important'};
  }

  .ant-modal-close {
    top: 0.4rem !important;
    right: 0.4rem !important;

    .ant-modal-close-x {
      font-size: 2rem !important;
    }
  }
`;

const rootDesktopStyles = css`
  .ant-modal-close {
    top: 0.8rem !important;
    right: 0.8rem !important;

    .ant-modal-close-x {
      font-size: 2rem !important;
    }
  }

  .ant-form-item {
    margin-bottom: 2.4rem;
  }

  .ant-form-item .ant-form-item-label > label {
    font-size: 1.8rem !important;
  }

  .ant-form-item-control-input-content > .ant-input,
  .ant-input-password > .ant-input {
    height: 4.8rem !important;
    font-size: 1.8rem !important;
  }

  .ant-input-affix-wrapper {
    font-size: ${isDesktop ? '2rem' : '1.4rem'} !important;
  }
`;
